import type { IStage } from '.';
import { repeat } from '../util';
import { z } from 'zod';

export const PlayAndConnectCardSchema = z.object({ name: z.string(), color: z.string().default('') });
export type PlayAndConnectCard = z.infer<typeof PlayAndConnectCardSchema>;
export const PlayAndConnectStarterCardSetSchema = z.tuple(repeat(7, PlayAndConnectCardSchema));
export const PlayAndConnectDataSchema = z
	.object({
		startingCards: z.tuple(repeat(12, PlayAndConnectStarterCardSetSchema)),
	})
	.default({ startingCards: repeat(12, repeat(7, { name: '', color: '' })) });

export type Stage = IStage | 'play-rounds' | 'play-ending-game' | 'share-contact';

export const PlayAndConnectRoundSchema = z.object({
	question: z.string(),
	rounds: z.number().default(1),
});
export type PlayAndConnectRound = z.infer<typeof PlayAndConnectRoundSchema>;

export const PlayAndConnectPresetSchema = z.object({
	presetName: z.string().default('No name'),
	rounds: z.array(PlayAndConnectRoundSchema).default([]),
});
export type PlayAndConnectPreset = z.infer<typeof PlayAndConnectPresetSchema>;

export interface PlayAndConnectPlayerState {
	id: string;
	name: string;
	color: string;
	score: number;
	lastScreen: PlayAndConnectUpdate;
	prompt?: PlayAndConnectUpdate;
	answers?: Array<{
		playerId: string;
		chosenPlayerId: string;
		secondChosenPlayerId?: string;
		answerText: string;
		timestamp: string;
		roundNumber?: number;
		subRoundNumber?: number;
	}>;
	companyOrFunction?: string;
	contact?: string;
	shareContact?: boolean;
}

export type PlayAndConnectState = {
	stage: Stage;
	roundIndex: number;
	subRoundIndex: number;
	currentQuestionRound: number;
	players: PlayAndConnectPlayerState[];
	numberOfPlayers: number;
	numberOfQuestions: number;
	questionRounds: number[];
	showPreface: boolean;
	questions: string[];
	answeredPlayerIds: string[];
	questionCompleted: boolean;
	finalQuestionActive: boolean;
	finalQuestionDone: boolean;
	timer?: {
		timerFrom: number;
		timerTo: number;
	};
};

export const PlayAndConnectUpdateSchema = z.discriminatedUnion('kind', [
	z.object({ kind: z.literal('trigger-effect'), effect: z.enum(['confetti', 'red-flash']), text: z.string().optional() }),
	z.object({ kind: z.literal('show-login-screen'), failed: z.boolean().optional() }),
	z.object({ kind: z.literal('show-register-screen'), numberOfPlayers: z.number().optional() }),
	z.object({
		kind: z.literal('show-question-screen'),
		question: z.string(),
		roundNumber: z.number(),
	}),
	z.object({
		kind: z.literal('show-select-player-screen'),
		question: z.string(),
		roundNumber: z.number(),
		players: z.object({ id: z.string(), name: z.string(), color: z.string() }).array(),
		timerFrom: z.number(),
		timerTo: z.number(),
	}),
	z.object({
		kind: z.literal('show-connect-players-screen'),
		question: z.string(),
		roundNumber: z.number(),
		players: z.object({ id: z.string(), name: z.string(), color: z.string() }).array(),
		timerFrom: z.number(),
		timerTo: z.number(),
		isFinalQuestion: z.boolean().optional(),
	}),
	z.object({
		kind: z.literal('show-swap-screen'),
		text: z.string().optional(),
		roundNumber: z.number(),
		question: z.string().optional(),
		questionNumber: z.number().optional(),
		timerFrom: z.number(),
		timerTo: z.number(),
	}),
	z.object({
		kind: z.literal('show-qualitypicker-screen'),
		title: z.string(),
		message: z.string(),
		cards: z.array(PlayAndConnectCardSchema),
	}),
	z.object({
		kind: z.literal('show-share-contact'),
		timerFrom: z.number(),
		timerTo: z.number(),
	}),
	z.object({
		kind: z.literal('show-results-screen'),
		players: z.array(z.object({ name: z.string(), color: z.string(), score: z.number() })),
	}),
	z.object({ kind: z.literal('show-message'), head: z.string(), body: z.string() }),
	z.object({
		kind: z.literal('show-wait-screen'),
		message: z.string(),
		nextQuestion: z.string().optional(),
		showMakeAnotherConnection: z.boolean().optional(),
	}),
	z.object({
		kind: z.literal('show-stop-screen'),
		message: z.string(),
		nextQuestion: z.string().optional(),
		nextQuestionNumber: z.number().optional(),
	}),

	z.object({
		kind: z.literal('update-players-state'),
		players: z.array(z.object({ name: z.string(), color: z.string() })),
		numberOfPlayers: z.number().optional(),
	}),
	z.object({
		kind: z.literal('update-player-state'),
		player: z.object({
			//
			name: z.string(),
			color: z.string(),
			score: z.number(),
		}),
	}),
	z.object({
		kind: z.literal('update-player-contact'),
		playerId: z.string(),
		contact: z.string().optional(),
		shareContact: z.boolean().optional(),
	}),
	z.object({
		kind: z.literal('display-trade-offer'),
		card: z.string(),
		player: z.object({ name: z.string(), color: z.string() }),
	}),

	z.object({
		kind: z.literal('display-purchase-request'),
		player: z.object({ name: z.string(), color: z.string() }),
	}),

	z.object({
		kind: z.literal('finalized-purchase'),
	}),
]);
export type PlayAndConnectUpdate = z.infer<typeof PlayAndConnectUpdateSchema>;

export const PlayAndConnectMessageSchema = z.discriminatedUnion('kind', [
	// TODO: make it so this can only be sent by admin
	z.object({ kind: z.literal('start-game'), numberOfQuestions: z.number().optional(), numberOfPlayers: z.number() }),
	z.object({ kind: z.literal('sync-number-of-players'), numberOfPlayers: z.number() }),
	z.object({ kind: z.literal('end-preface') }),
	z.object({ kind: z.literal('select-player'), playerId: z.string(), chosenPlayerId: z.string() }),
	z.object({ kind: z.literal('end-early') }),
	z.object({ kind: z.literal('end-early-day') }),
	z.object({ kind: z.literal('force-restart-round') }),
	z.object({ kind: z.literal('start-next-question') }),
	z.object({ kind: z.literal('start-round') }),
	z.object({ kind: z.literal('set-player-active'), playerId: z.string(), active: z.boolean() }),

	z.object({
		kind: z.literal('update-player-data'),
		playerId: z.string(),
		name: z.string(),
		color: z.string(),
		companyOrFunction: z.string().optional(),
		contact: z.string().optional(),
		shareContact: z.boolean().optional(),
	}),
	z.object({
		kind: z.literal('update-player-contact'),
		playerId: z.string(),
		contact: z.string().optional(),
		shareContact: z.boolean().optional(),
	}),

	z.object({ kind: z.literal('remove-player-data'), playerId: z.string() }),
	z.object({ kind: z.literal('double-found'), playerId: z.string(), otherPlayerColor: z.string(), cardName: z.string() }),

	z.object({
		kind: z.literal('send-answers'),
		playerId: z.string(),
		chosenPlayerId: z.string(),
		answer: z.string(),
	}),
	z.object({
		kind: z.literal('share-contact-details'),
		playerId: z.string(),
		contact: z.string().optional(),
		shareContact: z.boolean().optional(),
	}),
	z.object({
		kind: z.literal('send-final-answers'),
		playerId: z.string(),
		chosenPlayerId: z.string(),
		secondChosenPlayerId: z.string(),
		answer: z.string(),
	}),
	z.object({ kind: z.literal('restart-final-question'), playerId: z.string() }),
	z.object({ kind: z.literal('send-trade-offer'), playerId: z.string(), otherPlayerColor: z.string(), cardName: z.string() }),
	z.object({ kind: z.literal('finalize-trade'), playerId: z.string(), otherPlayerColor: z.string(), giveCard: z.string() }),

	z.object({ kind: z.literal('send-purchase-request'), playerId: z.string(), otherPlayerColor: z.string() }),
	z.object({ kind: z.literal('finalize-purchase'), playerId: z.string(), otherPlayerColor: z.string(), giveCard: z.string() }),
	z.object({ kind: z.literal('update-relevant-cards'), playerId: z.string(), relevantCards: z.array(z.string()) }),
]);

export type PlayAndConnectMessage = z.infer<typeof PlayAndConnectMessageSchema>;
